import { ClipboardCheck, Sigma, Target, TrendingUp } from 'lucide-react'
import { DashboardMetricCard, DashboardMetricGrid } from '@yeshuman/sdk'

import type { GoldenSetPulse } from '../../hooks/use-platform-pulse'

function formatRunDay(value: string | null | undefined): string {
  if (!value) return 'unknown date'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString()
}

export function GoldenSetPulseSummary({
  data,
  isLoading = false,
}: {
  data: GoldenSetPulse | undefined
  isLoading?: boolean
}) {
  if (!data?.has_run) {
    return (
      <p className="text-sm text-muted-foreground">
        No golden-set runs yet. Run{' '}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">
          python manage.py {data?.command || 'run_golden_set'}
        </code>{' '}
        to record a baseline.
      </p>
    )
  }

  const ratio = data.agreement_ratio
  const flagged = data.flagged ?? []
  const runLabel = `${data.mode || 'run'} · ${formatRunDay(data.run_at)}${data.stale ? ' · stale' : ''}`

  return (
    <>
      <DashboardMetricGrid maxColumns={4}>
        <DashboardMetricCard
          title="Agreement"
          value={ratio != null ? `${(ratio * 100).toFixed(1)}%` : '—'}
          description={`${data.agreed_count ?? 0} of ${data.labelled_count ?? 0} labelled pairs in an accepted band`}
          icon={Target}
          loading={isLoading}
        />
        <DashboardMetricCard
          title="Drift"
          value={data.mean_abs_drift != null ? data.mean_abs_drift.toFixed(3) : '—'}
          description={
            data.mean_abs_drift != null
              ? `Mean |Δ| · ${data.band_changes ?? 0} band change(s)${
                  data.distribution_shift != null
                    ? ` · shift ${data.distribution_shift.toFixed(2)}`
                    : ''
                }`
              : 'No baseline yet'
          }
          icon={TrendingUp}
          loading={isLoading}
        />
        <DashboardMetricCard
          title="Consistency"
          value={data.max_stdev != null ? `σ ${data.max_stdev.toFixed(3)}` : '—'}
          description={
            data.max_stdev == null
              ? 'Not sampled'
              : flagged.length
                ? `Max spread · ${flagged.length} pair(s) flagged`
                : 'Max spread · none flagged'
          }
          icon={Sigma}
          loading={isLoading}
        />
        <DashboardMetricCard
          title="Last run"
          value={data.golden_set_version || '—'}
          description={`${runLabel} · ${data.pair_count ?? 0} pairs${data.model ? ` · ${data.model}` : ''}`}
          icon={ClipboardCheck}
          loading={isLoading}
        />
      </DashboardMetricGrid>
      {flagged.length ? (
        <p className="text-sm text-muted-foreground">
          Flagged for variance: <span className="font-medium">{flagged.join(', ')}</span>
        </p>
      ) : null}
    </>
  )
}
